import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Button, Grid, CircularProgress, Box, Paper,
  Checkbox, FormControlLabel, Typography, Chip,
  useMediaQuery, useTheme,
} from "@mui/material";
import { PRESENTATION_RESOURCES } from "../talkTreeShared/PresentationGeneration/PresentationResources";
import ExploreMyTalk from '../talkTreeShared/ExploreMyTalk/ExploreMyTalk';
import { useTreeToSvgConverter } from "../talkTreeShared/PresentationGeneration/TreeToSvgConverter";
import SessionTrees from "../talkTreeShared/Tree/SessionTrees";
import FlowerTable from "../talkTreeShared/Tree/components/ThreeFlowers";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { generateFlowerKeyAltText } from "../talkTreeShared/Tree/components/treeAltText";
import { getSimulatedTrees } from "../talkTreeShared/Tree/utils/simulatedTreeStorage";

import { API_BASE_URL, MENU_IDS } from '../constants/constants';
import { getAuthToken, getLoggedInUser } from '../utils/auth';
import { COLORS, FONT_FAMILY, FONT_HEADING, BUTTON_HOVER_SX } from '../talkTreeShared/Tree/constants/theme';
import { markRealTreeCreated } from '../utils/onboarding';

const TAB_CONTENT_PADDING = '16px';
const LEFT_PANEL_BG = COLORS.page;
const LEFT_PANEL_BORDER = `1px solid ${COLORS.hairline}`;
const LEFT_PANEL_RADIUS = 2;

const WELCOME_KEY_PREFIX = 'tt_welcome_dismissed';
const welcomeKeyForUser = () => {
  const user = (getLoggedInUser() || '').toLowerCase().trim();
  return user ? `${WELCOME_KEY_PREFIX}:${user}` : null;
};

const NO_TALK_MESSAGE = 'No student talk was transcribed for this session, so no tree was generated.';
const TREE_LOAD_FAILED_MESSAGE = "We couldn't load this tree's image. Try deselecting it and selecting it again.";

function useWindowSize() {
  const [size, setSize] = useState({ width: window.innerWidth });
  useEffect(() => {
    const handler = () => setSize({ width: window.innerWidth });
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return size;
}

function getCompareImageStyle(screenWidth) {
  if (screenWidth > 1440) return { transform: 'scale(0.8)',    transformOrigin: 'center' };
  if (screenWidth > 1024) return { transform: 'scale(0.65)', transformOrigin: 'center' };
  if (screenWidth > 768)  return { transform: 'scale(0.5)', transformOrigin: 'center' };
  return                         { transform: 'scale(0.6)',  transformOrigin: 'center' }; 
}

function TalkTreeLibrary({ isTokenExpired, highlightedSessionId, setActiveMenu, onHighlightConsumed }) {
  const [data, setData] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [tutorTalkMoves, setTutorTalkMoves] = useState([]);
  const [studentTalkMoves, setStudentTalkMoves] = useState([]);
  const talkMovesLabels = ['Content Knowledge', 'Learning Community', 'Rigorous Thinking'];
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [showGraphs, setShowGraphs] = useState(false);
  const [mathTopicsCovered, setMathTopicsCovered] = useState('');
  const [feedbackSessionHighlights, setFeedbackSessionHighlights] = useState('');
  const [studentParticipation, setStudentParticipation] = useState([]);
  const [analyticstSessionID, setAnalyticsSessionID] = useState('');
  const [tutorName, setTutorName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [previousSessionIds, setPreviousSessionIds] = useState([]);
  const [client] = useState('Generic');
  const [analyticsNotFoundError, setAnalyticsNotFoundError] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedConversationStarters, setSelectedConversationStarters] = useState([]);
  const [selectedTreeForCelebrate, setSelectedTreeForCelebrate] = useState(null);
  const [selectedTreesForCompare, setSelectedTreesForCompare] = useState(new Set());
  const [celebrateTreeImage, setCelebrateTreeImage] = useState(null);
  const [compareTreeImages, setCompareTreeImages] = useState([]);
  const [selectedCompareConversationStarters, setSelectedCompareConversationStarters] = useState([]);
  const [selectedFeedbackTree, setSelectedFeedbackTree] = useState(null);
  const [feedbackTreeImage, setFeedbackTreeImage] = useState(null);
  const [mainTabIndex, setMainTabIndex] = useState(0);
  const [leftTree, setLeftTree] = useState(null);
  const [noTreeSessionIds, setNoTreeSessionIds] = useState(new Set());
  const markNoTree = (sessionId) => {
    setNoTreeSessionIds(prev => new Set(prev).add(sessionId));
    mixpanel.track('noTreeAvailable', { sessionId });
  };
  const clearNoTree = (sessionId) => setNoTreeSessionIds(prev => {
    if (!prev.has(sessionId)) return prev;
    const next = new Set(prev);
    next.delete(sessionId);
    return next;
  });
  const [rightTree, setRightTree] = useState(null);
  const dataFetched = useRef(false);
  const sessionsProcessed = useRef(false);
  const [highlightedTree, setHighlightedTree] = useState(null);
  const [noDataAvailable, setNoDataAvailable] = useState(false);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);
  const apiCache = useRef(new Map());
  const highlightHandled = useRef(null);

  const { width: screenWidth } = useWindowSize();

  const muiTheme = createTheme({
    typography: {
      fontFamily: FONT_FAMILY,
      h4: { fontFamily: FONT_HEADING },
      h5: { fontFamily: FONT_HEADING },
      h6: { fontFamily: FONT_HEADING },
    },
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { isConverting, errors, convertTreeToSvg, clearSvgData } = useTreeToSvgConverter({
    containerSelector: '.tree-container',
    options: { quality: 1, bgcolor: 'transparent', cacheBust: true }
  });

  // Dismiss and persist per user so it won't show again for this account
  const handleDismissWelcome = () => {
    try {
      const key = welcomeKeyForUser();
      if (key) localStorage.setItem(key, 'true');
    } catch { /* ignore storage errors */ }
    setShowWelcomeBanner(false);
  };

  const calculateSessionDuration = (metrics, sessionDiscourseHighlights) => {
    if (metrics.sessionTime && metrics.sessionTime > 0) return Math.round(metrics.sessionTime / 60);
    if (sessionDiscourseHighlights.length > 0)
      return Math.round(Math.max(...sessionDiscourseHighlights.map(h => h.end)) / 60);
    return 0;
  };

  const formatDisplayDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  };

  const findSessionInfo = (sessionId) => {
    if (!sessionId) return null;
    const real = data.find(item => item.sessionId === sessionId);
    if (real) return { startTime: real.startTime, title: real.sessionTitle };
    const simulated = getSimulatedTrees().find(t => t.id === sessionId);
    if (simulated) return { startTime: simulated.createdAt, title: simulated.title };
    return null;
  };

  const getNoTreeMessage = (sessionId) => {
    const session = data.find(item => item.sessionId === sessionId);
    return session?.emptyTranscript === true ? NO_TALK_MESSAGE : TREE_LOAD_FAILED_MESSAGE;
  };
  const handleTreeError = (e, sessionId) => {
    if (e?.noTreeAvailable) {
      markNoTree(sessionId);
    } else {
      console.error('Tree conversion failed', sessionId, e);
    }
  };

  const extractFeedbackContent = (infoBoxes, panelName) => {
    if (!infoBoxes?.length) return null;
    for (const box of infoBoxes) {
      if (box.boxContent && Array.isArray(box.boxContent)) {
        const panel = box.boxContent.find(c => c.panel === panelName);
        if (panel?.infoText) return panel.infoText.join('');
      }
    }
    return null;
  };

  const fetchTutorData = useCallback(async () => {
    if (dataFetched.current) return;
    dataFetched.current = true;
    if (isTokenExpired) await isTokenExpired();
    const authToken = getAuthToken();
    const tutorId = getLoggedInUser();
    if (!tutorId) return;
    setIsLoading(true);
    try {
      const [analyticsRes, statusRes] = await Promise.allSettled([
        axios.get(
          `${API_BASE_URL}v1aux/analysis/tutorDataAuthorized?tutorId=${tutorId}`,
          { headers: { Authorization: `${authToken}` } }
        ),
        fetch(
          `${API_BASE_URL}v1aux/mediaAnalysisStatus?userId=${encodeURIComponent(tutorId)}`,
          { method: 'GET', headers: { Authorization: authToken } }
        ).then(r => r.ok ? r.json() : [])
      ]);
      const tutorDataArray = analyticsRes.status === 'fulfilled' ? analyticsRes.value.data : [];
      if (!tutorDataArray?.length) {
        setIsLoading(false);
        setNoDataAvailable(true);
        return;
      }
      const descriptionMap = {};
      const emptyTranscriptMap = {};
      if (statusRes.status === 'fulfilled' && Array.isArray(statusRes.value)) {
        statusRes.value.forEach(s => {
          if (!s.sessionId) return;
          descriptionMap[s.sessionId] = s.description || '';
          emptyTranscriptMap[s.sessionId] = s.emptyTranscript === true;
        });
      }
      const tutor = tutorDataArray[0]?.sessionMetadata?.participants?.find(p => p.type === 'tutor');
      setTutorName(tutor ? tutor.name : tutorId);
      const processedData = tutorDataArray.map(session => ({
        sessionId: session.sessionId,
        tutorId: session.tutorId,
        region: session.sessionMetadata?.region || '',
        sessionTitle: session.sessionMetadata?.title || 'Untitled Session',
        startTime: session.sessionMetadata?.startTime || null,
        endTime: session.sessionMetadata?.endTime || null,
        metrics: session.metrics || {},
        participants: session.sessionMetadata?.participants || [],
        participantContributions: session.participantContributions || [],
        sessionMetadata: session.sessionMetadata || {},
        sessionDiscourseHighlights: session.sessionDiscourseHighlights || [],
        infoBoxes: session.infoBoxes || [],
        strengthsOpportunitiesFeedback: session.strengths_opportunities_feedback || [],
        description: descriptionMap[session.sessionId] || '',
        emptyTranscript: emptyTranscriptMap[session.sessionId] === true,
      }));

      const regionFromData = processedData[0]?.region || '';
      if (regionFromData) setSelectedRegion(regionFromData);

      setData(processedData);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching tutor data:', error);
      setIsLoading(false);
      setNoDataAvailable(true);
      dataFetched.current = false;
      mixpanel.track('talkTreeLibraryLoadFailed', { error: error.message || 'Unknown error' });
    }
  }, []);

  const processTranscriptData = useCallback(async (sessionId) => {
    if (!sessionId) return null;
    if (apiCache.current.has(sessionId)) return apiCache.current.get(sessionId);

    setIsLoading(true);
    try {
      const sessionData = data.find(item => item.sessionId === sessionId);
      if (!sessionData) throw new Error(`Session ${sessionId} not found`);
      const metrics = sessionData.metrics || {};
      const participantContributions = sessionData.participantContributions || [];
      const sessionMetadata = sessionData.sessionMetadata || {};
      const sessionDiscourseHighlights = sessionData.sessionDiscourseHighlights || [];

      const processedData = sessionDiscourseHighlights.map(h => ({
        ...h, statementId: h.segmentId, talkMoveCategory: h.category || '', talkMoveType: h.type || ''
      }));

      const participants = Array.isArray(sessionMetadata.participants) ? sessionMetadata.participants : [];
      const activeParticipants = participants
        .filter(p => p.type === 'student' && participantContributions.some(c => c.participantId === p.id))
        .map(p => ({ typeof: p.type, id: p.id, name: p.name }));

      const startTime = sessionMetadata.startTime || Date.now();
      const sessionDurationMinutes = calculateSessionDuration(metrics, sessionDiscourseHighlights);
      const sessionMeta = {
        activeParticipants, participants,
        sessionId: sessionMetadata.sessionId || sessionId,
        title: sessionMetadata.title || 'Untitled Session',
        tutorId: sessionMetadata.tutorId || '',
        startTime,
        endTime: startTime + sessionDurationMinutes * 60 * 1000,
        region: sessionMetadata.region || '',
        lessonSummary: sessionMetadata?.lessonSummary || '',
        subject: sessionMetadata?.subject || '',
        grade: sessionMetadata?.grade || '',
        feedbackSummary: extractFeedbackContent(sessionData.infoBoxes, 'feedbackSummary') || 'No summary available.',
        feedbackMathTopics: extractFeedbackContent(sessionData.infoBoxes, 'feedbackMathTopics') || 'No math topics available.',
        description: sessionData.description || sessionMetadata?.description || '',
      };
      setSelectedRegion(sessionMeta.region);
      const enrichedData = {
        sessionMetadata: sessionMeta,
        processedData,
        metrics: {
          tutorMetrics: { talkMoves: metrics.tutorTalkMoves || [], talkMovesCategories: metrics.tutorTalkmoveCategories || [], utteranceCount: metrics.tutorUtteranceCount || 0, talkTime: metrics.tutorTalkTime || 0, mathTermCount: metrics.tutorMathTermCount || 0 },
          studentMetrics: { talkMoves: metrics.studentTalkMoves || [], talkMovesCategories: metrics.studentTalkmoveCategories || [], utteranceCount: metrics.studentUtteranceCount || 0, talkTime: metrics.studentTalkTime || 0, mathTermCount: metrics.studentMathTermCount || 0 },
          sessionMetrics: { sessionTime: metrics.sessionTime || 0, sessionTimeMinutes: sessionDurationMinutes, totalUtterances: metrics.totalUtterances || 0, totalMathTermCount: metrics.totalMathTermCount || 0 }
        }
      };
      apiCache.current.set(sessionId, enrichedData);
      setIsLoading(false);
      return enrichedData;
    } catch (error) {
      setIsLoading(false);
      mixpanel.track('sessionDataProcessingFailed', { sessionId, error: error.message || 'Unknown error' });
      return null;
    }
  }, [data]);

  const loadAllSessions = async () => {
    if (!data.length || sessionsProcessed.current) return;
    sessionsProcessed.current = true;
    setAnalyticsLoading(true);
    try {
      const sortedSessions = [...data].sort((a, b) => a.startTime - b.startTime);
      const mostRecentId = sortedSessions[sortedSessions.length - 1].sessionId;
      const allOtherIds = sortedSessions.map(s => s.sessionId).filter(id => id !== mostRecentId);

      setAnalyticsSessionID(mostRecentId);
      setSelectedPeriod(mostRecentId);
      setPreviousSessionIds(allOtherIds);

      await processTranscriptData(mostRecentId);
      for (const id of allOtherIds) await processTranscriptData(id);

      setShowGraphs(true);
      mixpanel.track("talkTreesDisplayed", { treeCount: data.length });
      setAnalyticsLoading(false);
    } catch (error) {
      setAnalyticsLoading(false);
      sessionsProcessed.current = false;
    }
  };

  const handleCelebrateTreeSelection = async (sessionId) => {
    if (leftTree === sessionId) {
      setLeftTree(null); setSelectedTreeForCelebrate(null); setCelebrateTreeImage(null);
      setSelectedTreesForCompare(new Set(rightTree ? [rightTree] : []));
      setCompareTreeImages(prev => prev.filter(img => img.sessionId === rightTree));
      clearSvgData(sessionId);
    } else {
      setLeftTree(sessionId); setSelectedTreeForCelebrate(sessionId);
      setSelectedTreesForCompare(new Set(rightTree ? [sessionId, rightTree] : [sessionId]));
      try {
        await new Promise(r => setTimeout(r, 200));
        const svg = await convertTreeToSvg(sessionId);
        clearNoTree(sessionId);
        setCelebrateTreeImage(svg);
        setCompareTreeImages(prev => [{ sessionId, image: svg }, ...prev.filter(img => img.sessionId !== sessionId)]);
      } catch (e) { handleTreeError(e, sessionId); }
    }
  };

  const handleCompareTreeSelection = async (sessionId) => {
    if (leftTree === sessionId) {
      setLeftTree(null); setSelectedTreeForCelebrate(null); setCelebrateTreeImage(null);
      setSelectedTreesForCompare(new Set(rightTree ? [rightTree] : []));
      setCompareTreeImages(prev => prev.filter(img => img.sessionId === rightTree));
      clearSvgData(sessionId);
      if (selectedFeedbackTree === sessionId) { setSelectedFeedbackTree(null); setFeedbackTreeImage(null); }
      return;
    }
    if (rightTree === sessionId) {
      setRightTree(null);
      setSelectedTreesForCompare(new Set(leftTree ? [leftTree] : []));
      setCompareTreeImages(prev => prev.filter(img => img.sessionId === leftTree));
      clearSvgData(sessionId);
      return;
    }
    if (!leftTree) {
      setLeftTree(sessionId);
      setSelectedTreeForCelebrate(sessionId);
      setSelectedFeedbackTree(sessionId);
      setSelectedTreesForCompare(new Set(rightTree ? [sessionId, rightTree] : [sessionId]));
      try {
        await new Promise(r => setTimeout(r, 200));
        const svg = await convertTreeToSvg(sessionId);
        clearNoTree(sessionId);
        setCelebrateTreeImage(svg);
        setFeedbackTreeImage(svg);
        setCompareTreeImages(prev => [
          { sessionId, image: svg },
          ...prev.filter(img => img.sessionId !== sessionId),
        ]);
      } catch (e) {
        handleTreeError(e, sessionId);
      }
      return;
    }
    if (!rightTree) {
      setRightTree(sessionId);
      setSelectedTreesForCompare(new Set([leftTree, sessionId]));
      try {
        await new Promise(r => setTimeout(r, 200));
        const svg = await convertTreeToSvg(sessionId);
        clearNoTree(sessionId);
        setCompareTreeImages(prev => [...prev, { sessionId, image: svg }]);
      } catch (e) {
        handleTreeError(e, sessionId);
      }
      return;
    }
    const oldRight = rightTree;
    setRightTree(sessionId);
    setSelectedTreesForCompare(new Set([leftTree, sessionId]));
    clearSvgData(oldRight);
    try {
      await new Promise(r => setTimeout(r, 200));
      const svg = await convertTreeToSvg(sessionId);
      clearNoTree(sessionId);
      setCompareTreeImages(prev => [
        ...prev.filter(img => img.sessionId !== oldRight),
        { sessionId, image: svg },
      ]);
    } catch (e) {
      handleTreeError(e, sessionId);
    }
  };

  const handleFeedbackTreeSelection = async (sessionId) => {
    if (!sessionId) return;
    if (selectedFeedbackTree === sessionId) {
      setSelectedFeedbackTree(null); setFeedbackTreeImage(null); clearSvgData(sessionId);
      setLeftTree(null); setSelectedTreeForCelebrate(null); setCelebrateTreeImage(null);
    } else {
      setSelectedFeedbackTree(sessionId); setLeftTree(sessionId); setSelectedTreeForCelebrate(sessionId);
      try {
        await new Promise(r => setTimeout(r, 200));
        const svg = await convertTreeToSvg(sessionId);
        clearNoTree(sessionId);
        setFeedbackTreeImage(svg); setCelebrateTreeImage(svg);
        setCompareTreeImages(prev => [{ sessionId, image: svg }, ...prev.filter(img => img.sessionId !== sessionId)]);
      } catch (e) { handleTreeError(e, sessionId); }
    }
  };

  const onTreeSelect = (sessionId, source = 'manual') => {
    const tabNames = ['Introduction', 'Celebrate', 'Compare', 'Feedback'];

    mixpanel.track("talkTreeSelected", {
      page: 'TalkTreeLibrary',
      sessionId: sessionId,
      currentTab: tabNames[mainTabIndex],
      tabWillSwitchTo: mainTabIndex === 0 ? 'Celebrate' : tabNames[mainTabIndex],
      source,
    });
    
    if (mainTabIndex === 0) { setMainTabIndex(1); handleCelebrateTreeSelection(sessionId); }
    else if (mainTabIndex === 1) handleCelebrateTreeSelection(sessionId);
    else if (mainTabIndex === 2) handleCompareTreeSelection(sessionId);
    else if (mainTabIndex === 3) handleFeedbackTreeSelection(sessionId);
  };

  const handleTabChange = (i) => {
    const tabNames = ['Introduction', 'Celebrate', 'Compare', 'Feedback'];
    mixpanel.track("tabSwitched", {
      page: 'TalkTreeLibrary',
      fromTab: tabNames[mainTabIndex],
      toTab: tabNames[i],
    });
    setMainTabIndex(i);
  };

  const generateIntroductionPresentation = () => {
    mixpanel.track("presentationGenerated", { page: 'TalkTreeLibrary', presentationType: "Introduction" });
    sessionStorage.setItem('presentationData', JSON.stringify({ mode: 'introduction', tutorName, introContent: PRESENTATION_RESOURCES.tabs.introduction.content, client }));
    window.open('/presentation', '_blank');
  };

  const generateCelebratePresentation = async () => {
    const sessionData = findSessionInfo(selectedTreeForCelebrate);
    const scale = screenWidth > 1440 ? 0.8 : screenWidth > 1024 ? 0.65 : screenWidth > 768 ? 0.5 : 0.6;

    mixpanel.track("presentationGenerated", { page: 'TalkTreeLibrary', presentationType: "Celebrate", sessionId: selectedTreeForCelebrate, selectedConversationStarters: selectedConversationStarters });
    
    sessionStorage.setItem('presentationData', JSON.stringify({ 
      mode: 'celebrate', 
      selectedTree: { sessionId: selectedTreeForCelebrate, startTime: sessionData?.startTime, image: celebrateTreeImage }, 
      selectedStarters: selectedConversationStarters, 
      tutorName, 
      client,
      treeScale: scale
    }));
    window.open('/presentation', '_blank');
  };

  const generateComparePresentation = async () => {
    const selectedTreeData = await Promise.all(Array.from(selectedTreesForCompare).map(async (sessionId) => {
      const sessionData = findSessionInfo(sessionId);
      return { sessionId, startTime: sessionData?.startTime, image: compareTreeImages.find(img => img.sessionId === sessionId)?.image };
    }));
    
    const scale = screenWidth > 1440 ? 0.8 : screenWidth > 1024 ? 0.65 : screenWidth > 768 ? 0.5 : 0.6;
    
    mixpanel.track("presentationGenerated", { page: 'TalkTreeLibrary', presentationType: "Compare", sessionIds: Array.from(selectedTreesForCompare), selectedConversationStarters: selectedCompareConversationStarters });
    
    sessionStorage.setItem('presentationData', JSON.stringify({ 
      mode: 'compare', 
      selectedTrees: selectedTreeData, 
      selectedStarters: selectedCompareConversationStarters, 
      tutorName, 
      client,
      treeScale: scale
    }));
    window.open('/presentation', '_blank');
  };

  const TabContentWrapper = ({ children }) => (
    <Box sx={{ padding: isMobile ? '5px' : TAB_CONTENT_PADDING, textAlign: 'left' }}>
      {children}
    </Box>
  );

  const LeftPanel = ({ children }) => (
    <Grid item xs={12} md={6} sx={{ border: LEFT_PANEL_BORDER, borderRadius: LEFT_PANEL_RADIUS, backgroundColor: LEFT_PANEL_BG, overflow: isMobile ? 'visible' : 'hidden' }}>
      {children}
    </Grid>
  );

  const RightPanel = ({ children }) => (
    <Grid item xs={12} md={6}>
      <Box sx={{ p: isMobile ? 1 : 2, height: '100%', display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
        {children}
      </Box>
    </Grid>
  );

  const GenerateButton = ({ onClick, disabled, label }) => (
    <Box sx={{ mt: 'auto', pt: 2, display: 'flex', justifyContent: 'center' }}>
      <Button
        variant="contained"
        onClick={onClick}
        disabled={disabled}
        aria-label={`Generate ${label} presentation`}
        sx={{
          backgroundColor: COLORS.primary,
          color: '#fff',
          textTransform: 'none',
          fontWeight: 600,
          fontFamily: FONT_FAMILY,
          borderRadius: '4px',
          fontSize: isMobile ? '13px' : '14px',
          px: 3,
          minHeight: '44px',
          width: isMobile ? '100%' : 'auto',
          '&:hover': BUTTON_HOVER_SX,
          '&.Mui-disabled': { backgroundColor: COLORS.disabled, color: '#fff' },
          '&:focus-visible': { outline: `3px solid ${COLORS.primary}`, outlineOffset: '2px' },
        }}
      >
        {label}
      </Button>
    </Box>
  );

  const DeselectButton = ({ onClick, ariaLabel }) => (
    <Box
      component="button"
      onClick={onClick}
      aria-label={ariaLabel}
      sx={{
        ml: 'auto',
        flexShrink: 0,
        background: 'none', border: 'none', cursor: 'pointer',
        color: COLORS.textMuted, p: '2px', lineHeight: 0,
        '&:hover': { color: COLORS.error },
        '&:focus-visible': { outline: `2px solid ${COLORS.primary}`, borderRadius: '4px' },
      }}
    >
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    </Box>
  );

  const renderIntroductionTab = () => (
    <TabContentWrapper>
      <Grid container spacing={2} alignItems="flex-start">
        <LeftPanel>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 2, minHeight: isMobile ? '200px' : '100%' }}>
            <Typography sx={{ fontSize: '1.25rem' }} gutterBottom>Sample Talk Tree</Typography>
            <img
              src="/SampleTree.png"
              alt="Sample talk tree showing branches with colored flowers representing student talk moves"
              style={{ maxWidth: '100%', height: 'auto', maxHeight: isMobile ? '250px' : '400px' }}
            />
          </Box>
          <FlowerTable isPresentationPage={true} role="img" aria-label={generateFlowerKeyAltText()} />
        </LeftPanel>

        <RightPanel>
          {PRESENTATION_RESOURCES.tabs.introduction.pages.map((page, i) => (
            <Box key={i} sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom> {page.content.heading} </Typography>
              {page.content.description.length > 0 && (
                <Typography variant="body1" paragraph>{page.content.description}</Typography>
              )}
              <Typography variant="body1" component="div">
                <ul style={{ paddingLeft: '1.5em', margin: 0 }}>
                  {page.content.pointList.map((item, j) => <li key={j}>{item}</li>)}
                </ul>
              </Typography>
            </Box>
          ))}
          <GenerateButton onClick={generateIntroductionPresentation} label={PRESENTATION_RESOURCES.messages.generatePresentation} />
        </RightPanel>
      </Grid>
    </TabContentWrapper>
  );

  const renderCelebrateTab = () => {
    const handleStarterChange = (starter, checked) =>
      setSelectedConversationStarters(prev => checked ? [...prev, starter] : prev.filter(s => s !== starter));

    return (
      <TabContentWrapper>
        {isConverting && (
          <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, bgcolor: PRESENTATION_RESOURCES.styles.loadingBackground, borderRadius: 1 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} aria-hidden="true" />
            <Typography variant="body2">Converting tree to image...</Typography>
          </Box>
        )}
        {errors.length > 0 && (
          <Box role="alert" sx={{ mb: 2, p: 2, bgcolor: PRESENTATION_RESOURCES.styles.errorBackground, borderRadius: 1 }}>
            <Typography variant="body2" color="error">Error: {errors[errors.length - 1].error.message}</Typography>
          </Box>
        )}

        <Grid container spacing={2} alignItems="flex-start">
          <LeftPanel>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              p: 2,
            }}>
              {leftTree && celebrateTreeImage ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, width: '100%', flexWrap: 'nowrap', position: 'relative' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                      {formatDisplayDate(new Date(findSessionInfo(leftTree)?.startTime))}
                    </Typography>
                    <Box sx={{ position: 'absolute', right: '20px' }}>
                      <DeselectButton
                        onClick={() => handleCelebrateTreeSelection(leftTree)}
                        ariaLabel="Deselect this tree"
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                  <img
                    src={celebrateTreeImage}
                    alt={`Talk tree for session on ${formatDisplayDate(new Date(findSessionInfo(leftTree)?.startTime))}`}
                    style={{
                      display: 'block',
                      objectFit: 'contain',
                      objectPosition: 'top center',
                      maxHeight: '100%',
                      transition: 'transform 0.2s ease',
                      ...getCompareImageStyle(screenWidth),
                    }}
                  />
                </Box>
                </>
              ) : leftTree && noTreeSessionIds.has(leftTree) ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, width: '100%', flexWrap: 'nowrap', position: 'relative' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                      {formatDisplayDate(new Date(findSessionInfo(leftTree)?.startTime))}
                    </Typography>
                    <Box sx={{ position: 'absolute', right: '20px' }}>
                      <DeselectButton
                        onClick={() => handleCelebrateTreeSelection(leftTree)}
                        ariaLabel="Deselect this tree"
                      />
                    </Box>
                  </Box>
                  <Box role="status" aria-label="No tree available" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', p: 2, minHeight: isMobile ? '150px' : '300px', width: '90%' }}>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ textAlign: 'center' }}>
                      {getNoTreeMessage(leftTree)}
                    </Typography>
                    {data.find(item => item.sessionId === leftTree)?.emptyTranscript === true && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                        Try selecting another session in the Library.
                      </Typography>
                    )}
                  </Box>
                </>
              ) : (
                <Box role="status" aria-label="No tree selected" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', border: LEFT_PANEL_BORDER, borderRadius: 1, p: 2, minHeight: isMobile ? '150px' : '300px', width: '90%' }}>
                  <Typography variant="subtitle1" color="text.secondary">Click a tree in the Library to see it here</Typography>
                </Box>
              )}
            </Box>
            <FlowerTable isPresentationPage={true} role="img" aria-label={generateFlowerKeyAltText()} />
          </LeftPanel>

          <RightPanel>
            <Typography variant="h6"  sx={{  fontWeight: '400', fontSize: '1.25rem' }} gutterBottom> {PRESENTATION_RESOURCES.tabs.celebrate.title} </Typography>
            <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>{PRESENTATION_RESOURCES.tabs.celebrate.description}</Typography>
            <Typography variant="h6"  sx={{  fontWeight: '400', fontSize: '1.25rem' }} gutterBottom>{PRESENTATION_RESOURCES.tabs.celebrate.conversationStarterTitle}</Typography>
            <Box role="group" aria-label="Conversation starters">
              {PRESENTATION_RESOURCES.tabs.celebrate.conversationStarters.map((starter, i) => (
                <FormControlLabel
                  key={i}
                  control={
                    <Checkbox
                      checked={selectedConversationStarters.includes(starter)}
                      onChange={(e) => handleStarterChange(starter, e.target.checked)}
                      inputProps={{ 'aria-label': starter }}
                      sx={{ '&:focus-visible': { outline: `3px solid ${COLORS.primary}` } }}
                    />
                  }
                  label={starter}
                  sx={{ display: 'block', mb: 1 }}
                />
              ))}
            </Box>
            <GenerateButton
              onClick={() => { if (!selectedTreeForCelebrate) { alert(PRESENTATION_RESOURCES.messages.selectTree); return; } generateCelebratePresentation(); }}
              disabled={!selectedTreeForCelebrate || isConverting}
              label={PRESENTATION_RESOURCES.messages.generatePresentation}
            />
          </RightPanel>
        </Grid>
      </TabContentWrapper>
    );
  };

  const renderCompareTab = () => {
    const handleStarterChange = (starter, checked) =>
      setSelectedCompareConversationStarters(prev => checked ? [...prev, starter] : prev.filter(s => s !== starter));

    const imageStyle = {
      display: 'block',
      objectFit: 'contain',
      objectPosition: 'center',
      maxHeight: '100%',
      transition: 'transform 0.2s ease',
      ...getCompareImageStyle(screenWidth),
    };

    return (
      <TabContentWrapper>
        {isConverting && (
          <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, bgcolor: PRESENTATION_RESOURCES.styles.loadingBackground, borderRadius: 1 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} aria-hidden="true" />
            <Typography variant="body2">Converting trees to images...</Typography>
          </Box>
        )}

        <Grid container spacing={2} alignItems="flex-start">
          <LeftPanel>
            <Box sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}>
              {[{ key: 'left', tree: leftTree }, { key: 'right', tree: rightTree }].map(({ key, tree }) => (
                <Box
                  key={key}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    boxSizing: 'border-box',
                  }}
                >
                  {tree && compareTreeImages.find(img => img.sessionId === tree) ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, width: '100%', flexWrap: 'nowrap', position: 'relative', minHeight: '32px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.65rem' : undefined, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                          {formatDisplayDate(new Date(findSessionInfo(tree)?.startTime))}
                        </Typography>
                        <Box sx={{ position: 'absolute', right: '20px' }}>
                          <DeselectButton
                            onClick={() => handleCompareTreeSelection(tree)}
                            ariaLabel={`Deselect ${key === 'left' ? 'Selection 1' : 'Selection 2'}`}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%'}}>
                      <img
                        src={compareTreeImages.find(img => img.sessionId === tree).image}
                        alt={`${key === 'left' ? 'Selection 1' : 'Selection 2'} talk tree for comparison, session on ${formatDisplayDate(new Date(findSessionInfo(tree)?.startTime))}`}
                        style={imageStyle}
                      />
                      </Box>
                    </>
                  ) : tree && noTreeSessionIds.has(tree) ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, width: '100%', flexWrap: 'nowrap', position: 'relative', minHeight: '32px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.65rem' : undefined, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                          {formatDisplayDate(new Date(findSessionInfo(tree)?.startTime))}
                        </Typography>
                        <Box sx={{ position: 'absolute', right: '20px' }}>
                          <DeselectButton
                            onClick={() => handleCompareTreeSelection(tree)}
                            ariaLabel={`Deselect ${key === 'left' ? 'Selection 1' : 'Selection 2'}`}
                          />
                        </Box>
                      </Box>
                      <Box role="status" aria-label="No tree available" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', p: 1, minHeight: isMobile ? '80px' : '200px' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : undefined, textAlign: 'center' }}>
                          {getNoTreeMessage(tree)}
                          {data.find(item => item.sessionId === tree)?.emptyTranscript === true ? ' Try selecting another session.' : ''}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Box role="status" aria-label={key === 'left' ? 'No first tree selected' : 'No second tree selected'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', p: 1, minHeight: isMobile ? '80px' : '200px' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : undefined }}>
                        {key === 'left' ? 'Click a tree in the Library to see it here' : 'Click another tree in the Library to see it here.'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
            <FlowerTable isPresentationPage={true} role="img" aria-label={generateFlowerKeyAltText()} />
          </LeftPanel>

          <RightPanel>
            <Typography variant="h6"  sx={{  fontWeight: '400', fontSize: '1.25rem' }} gutterBottom> {PRESENTATION_RESOURCES.tabs.compare.title} </Typography>
            <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>{PRESENTATION_RESOURCES.tabs.compare.description}</Typography>
            <Typography variant="h6" sx={{  fontWeight: '400', fontSize: '1.25rem' }} gutterBottom> {PRESENTATION_RESOURCES.tabs.compare.conversationStarterTitle} </Typography>
            <Box role="group" aria-label="Comparison conversation starters">
              {PRESENTATION_RESOURCES.tabs.compare.conversationStarters.map((starter, i) => (
                <FormControlLabel
                  key={i}
                  control={
                    <Checkbox
                      checked={selectedCompareConversationStarters.includes(starter)}
                      onChange={(e) => handleStarterChange(starter, e.target.checked)}
                      inputProps={{ 'aria-label': starter }}
                    />
                  }
                  label={starter}
                  sx={{ display: 'block', mb: 1 }}
                />
              ))}
            </Box>
            <GenerateButton
              onClick={() => { if (!leftTree || !rightTree) { alert('Please select exactly 2 trees for comparison.'); return; } generateComparePresentation(); }}
              disabled={isConverting || !leftTree || !rightTree}
              label={PRESENTATION_RESOURCES.messages.generatePresentation}
            />
          </RightPanel>
        </Grid>
      </TabContentWrapper>
    );
  };

  useEffect(() => { fetchTutorData(); }, []);

  useEffect(() => {
    if (data.length > 0 && !sessionsProcessed.current) {
      loadAllSessions();
    } else if (data.length > 0 && sessionsProcessed.current) {
      setShowGraphs(true);
    }
  }, [data]);

  // Arriving from "Manage Trees" with a session to highlight/select.
  // We must wait until the library (SessionTrees) is actually mounted, otherwise the
  // tree DOM element doesn't exist yet and SVG capture fails.
  useEffect(() => {
    if (!highlightedSessionId) return;
    if (isLoading || analyticsLoading || !showGraphs) return;   // wait for SessionTrees to mount
    if (highlightHandled.current === highlightedSessionId) return;

    const stillExists =
      data.some(item => item.sessionId === highlightedSessionId) ||
      getSimulatedTrees().some(t => t.id === highlightedSessionId);

    if (!stillExists) {
      // The tree isn't in the library data (e.g. still processing). Nothing to select.
      console.log(`[TalkTreeLibrary] No tree available in the library for session ${highlightedSessionId}`);
      onHighlightConsumed?.();
      return;
    }

    highlightHandled.current = highlightedSessionId;
    setHighlightedTree(highlightedSessionId);

    // SessionTrees only renders the current page, so setting highlightedTree makes it
    // jump to the page holding this tree. Give that page a moment to mount before we
    // select (and capture) the tree; the converter then polls for the element as well.
    setTimeout(() => onTreeSelect(highlightedSessionId, 'upload_redirect'), 300);

    // Intentionally not cleaned up on re-run: with the extra deps above, a cleanup would
    // cancel this timer on the next state change and the highlight would never be consumed.
    setTimeout(() => {
      setHighlightedTree(null);
      onHighlightConsumed?.();
      highlightHandled.current = null;
    }, 3000);
  }, [highlightedSessionId, data, isLoading, analyticsLoading, showGraphs]);

  useEffect(() => {
    if (mainTabIndex === 3 && !selectedFeedbackTree && leftTree) handleFeedbackTreeSelection(leftTree);
  }, [mainTabIndex]);

  useEffect(() => {
  if (isLoading) return;
    const hasRealBackendTree = data.some(session =>
      !session.sessionTitle?.trim().toLowerCase().startsWith('sample')
    );
    const hasSimulatedTree = getSimulatedTrees().length > 0;
    if (hasRealBackendTree || hasSimulatedTree) markRealTreeCreated();

    // Welcome banner only while the library holds just the sample trees
    let dismissed = false;
    try {
      const key = welcomeKeyForUser();
      dismissed = !key || localStorage.getItem(key) === 'true';
    } catch { /* ignore storage errors */ }
    setShowWelcomeBanner(!dismissed && !hasRealBackendTree && !hasSimulatedTree);
  }, [data, isLoading]);

  const IntroIcon = ({ active }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_2461_6427)">
    <g clipPath="url(#clip1_2461_6427)">
    <path d="M18.6727 17.8057C18.6727 17.8057 17.7117 17.1065 17.5383 16.9773C17.336 16.8254 17.0903 16.6278 16.8591 16.3694C17.9212 16.0312 18.7486 15.6816 19.5578 15.2333C20.3634 15.5828 21.9964 14.7203 21.9964 14.7203C21.0137 14.7963 20.7536 14.7089 20.4682 14.6405L20.479 14.6329C20.8475 14.3631 21.3497 13.5994 21.4075 13.5196L21.8158 12.6836L20.9993 13.3866C20.6091 13.7361 20.013 14.0743 19.4711 14.3517C16.0788 16.0996 12.8092 16.016 12.8092 16.016L12.6647 10.2632C13.8353 10.1378 14.9082 9.94018 15.9379 9.65899C16.7905 10.7951 17.8382 11.2207 19.1351 10.9585C19.1351 10.9585 18.2536 10.6887 17.7731 10.3809C17.0325 9.90218 16.9169 9.4766 16.8158 9.37781C18.521 8.42027 19.1857 7.42473 19.1857 7.42473C18.8714 7.62232 18.4957 7.8465 18.0766 8.01749C17.0506 8.43927 15.8764 8.73945 14.3916 8.96363C14.3736 8.71665 14.3374 8.22648 14.3663 7.8693C14.4458 6.89276 14.7095 6.33419 14.7095 6.33419C14.0231 6.96496 13.6004 7.97569 13.5643 9.06623L12.6395 9.14982C12.6105 7.67171 12.5311 4.05813 12.4841 3.82635C12.4841 3.82635 12.3793 1.81627 12.3071 0.672539C12.2348 -0.471192 12.0108 4.6243 12.0108 4.6243L11.9458 6.75597C11.328 6.68757 10.8475 6.61538 10.2442 6.48998C10.4646 4.95488 10.1539 4.5711 9.81431 4.05053C9.81431 4.05053 9.84682 5.47925 9.62645 6.2924C8.56069 6.00361 7.37933 5.64644 7.3685 5.64264C7.3685 5.64264 7.96821 6.48998 9.73121 7.21194C10.8367 7.53492 11.5556 7.64131 11.9314 7.69831L11.8013 12.9002C9.98049 12.6912 8.33671 12.315 6.79769 11.7489C6.93858 9.96297 6.02457 9.14222 6.02457 9.14222C6.02457 9.14222 6.21243 10.229 5.99205 11.4373C3.74494 10.5785 2.72977 9.602 2.72977 9.602C3.23194 10.7191 3.71604 11.2473 5.37428 12.1402C5.87645 12.41 6.38584 12.638 6.87355 12.8356C6.39668 13.037 5.30202 13.3828 5.30202 13.3828C5.30202 13.3828 6.64957 13.6639 8.00795 13.2498C9.22905 13.6449 10.4646 13.9299 11.776 14.1123L11.6496 19.1394L9.76373 18.8924C9.56142 18.6682 8.97977 17.9539 8.54624 17.4219C7.88873 16.6126 7.81647 16.529 7.77312 16.4948L7.2565 16.0692C7.2565 16.0692 8.00434 18.1515 8.37645 18.6416C8.06575 18.5808 7.74422 18.5087 7.38656 18.4213L7.23121 18.3833C6.59899 18.2275 5.45737 17.8589 5.15751 17.7563C5.00939 17.5359 4.53974 16.8406 4.41691 16.6392L4.20014 16.282C4.20014 16.282 4.15318 16.4264 4.16402 16.7038C4.18569 17.1635 4.25434 17.2471 4.31575 17.4371C3.33309 17.1407 2.53107 16.7038 2.53107 16.7038L3.23194 17.4257C4.87934 18.8126 8.91113 19.8272 8.91113 19.8272C9.79986 20.059 10.6705 20.1919 11.6207 20.3287L11.5701 21.8069C8.9328 21.8904 6.90246 22.3996 6.90246 23.0114H17.802C17.802 22.3844 15.6777 21.8676 12.9501 21.7993L12.849 17.2129C14.0014 17.0761 14.9986 16.89 15.9559 16.6392C16.9819 17.7145 18.6835 17.8171 18.6835 17.8171L18.6727 17.8057Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    <path d="M11.7038 4.04675C12.0036 2.81942 11.3353 1.63389 10.1973 1.31091C9.8685 2.48124 10.5477 3.75416 11.7038 4.04675Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    <path d="M5.00217 12.2467C4.18208 11.3196 2.87066 11.2702 2 12.0985C2.75867 13.0219 4.14595 13.1092 5.00217 12.2467Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    <path d="M5.25145 15.2408C4.92269 16.4112 5.60188 17.6841 6.75795 17.9767C7.0578 16.7494 6.38945 15.5638 5.25145 15.2408Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    <path d="M10.8728 18.6759C11.1727 17.4486 10.5043 16.263 9.36633 15.9401C9.03757 17.1104 9.71676 18.3833 10.8728 18.6759Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    <path d="M17.2999 9.45C17.9971 10.4607 19.3772 10.6317 20.2803 9.86797C19.5614 8.88763 18.2608 8.69005 17.2999 9.45Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    <path d="M13.0332 19.1888C14.1568 19.5916 15.3779 18.9 15.6994 17.7297C14.5759 17.3611 13.4053 17.9843 13.0332 19.1888Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    <path d="M17.6105 16.7303C18.4957 17.5625 19.8793 17.4257 20.6091 16.4795C19.7095 15.6816 18.4017 15.7766 17.6105 16.7303Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    <path d="M17.1879 8.08594C18.3584 7.87695 19.1207 6.65722 18.8714 5.4679C17.7153 5.70728 16.9711 6.84341 17.1879 8.08594Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    <path d="M6.57731 15.0015C7.60694 15.5639 8.92558 15.1041 9.42052 13.968C8.33309 13.4246 7.09754 13.8882 6.57731 15.0015Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    <path d="M9.22182 5.88209C9.6698 4.70796 9.15318 3.43884 8.06575 2.96387C7.5961 4.081 8.11272 5.43372 9.22182 5.88209Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    <path d="M5.44653 10.8103C5.74639 9.58297 5.07803 8.39744 3.94003 8.07446C3.61127 9.24479 4.29046 10.5177 5.44653 10.8103Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    <path d="M14.4819 0C13.3259 0.239385 12.5816 1.37552 12.7984 2.61804C13.9689 2.40905 14.7312 1.18933 14.4819 0Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    <path d="M18.2464 14.3897C19.4061 14.3631 20.3562 13.2992 20.2731 12.0529C19.0701 12.1213 18.1958 13.151 18.2464 14.3897Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    <path d="M14.2218 4.80672C13.9689 5.0689 13.7522 5.31969 13.5715 5.60087L13.4234 5.95045C13.2247 6.36842 14.6734 6.20123 14.8577 6.07204C15.0708 6.56601 15.3309 6.99919 15.7753 7.27657C16.0679 6.90419 16.1691 6.33043 16.1871 5.88965C16.1871 5.83266 16.3353 5.77186 16.3895 5.76806C16.8266 5.73386 17.2746 5.55907 17.6142 5.25509C17.3685 4.87511 16.9133 4.57113 16.5267 4.44194C16.4798 4.42674 16.4147 4.31275 16.422 4.26715C16.5159 3.86438 16.5159 3.30581 16.3497 2.85364C15.8656 2.99043 15.4935 3.35521 15.172 3.79598C14.7204 3.4882 14.2435 3.32101 13.7197 3.34761C13.7197 3.88338 13.9184 4.36975 14.2218 4.81052V4.80672ZM15.1539 4.36975C15.4682 4.24435 15.8186 4.40774 15.9379 4.73832C16.0571 5.0689 15.9017 5.43748 15.5874 5.56287C15.2731 5.68827 14.9227 5.52488 14.8035 5.1943C14.6842 4.86372 14.8396 4.49514 15.1539 4.36975Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    <path d="M16.6676 13.0826C17.0289 12.8204 17.3324 12.4329 17.4769 11.9883C17.0795 11.7907 16.5448 11.7717 16.1474 11.8629C16.1004 11.8743 15.9848 11.8097 15.974 11.7679C15.862 11.3689 15.591 10.8864 15.2334 10.5862C14.8793 10.9624 14.7348 11.4753 14.6734 12.0263C14.1387 11.9997 13.6438 12.1099 13.2066 12.4139C13.4631 12.8774 13.8714 13.189 14.341 13.4094C14.2507 13.7704 14.1821 14.101 14.164 14.4391L14.2038 14.8191C14.2327 15.2865 15.4032 14.3708 15.5007 14.1618C15.9234 14.4733 16.3569 14.7127 16.8699 14.7127C16.9458 14.2378 16.7579 13.6868 16.5556 13.2992C16.5303 13.2498 16.6243 13.1206 16.6712 13.0864L16.6676 13.0826ZM15.8801 13.3296C15.6705 13.607 15.2876 13.6488 15.0275 13.4322C14.7637 13.2118 14.724 12.809 14.9299 12.5355C15.1394 12.2581 15.5224 12.2163 15.7825 12.4329C16.0426 12.6495 16.086 13.056 15.8801 13.3296Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    <path d="M10.6814 9.97814C11.0426 9.71595 11.3461 9.32838 11.4906 8.8838C11.0932 8.68622 10.5585 8.66722 10.1611 8.75841C10.1142 8.76981 9.99855 8.70521 9.98772 8.66342C9.87572 8.26444 9.60477 7.78187 9.24711 7.48169C8.89306 7.85787 8.74855 8.37083 8.68714 8.9218C8.15246 8.8952 7.65751 9.0054 7.22038 9.30938C7.47688 9.77295 7.88512 10.0845 8.35477 10.3049C8.26445 10.6659 8.19581 10.9965 8.17775 11.3347L8.21748 11.7146C8.24639 12.182 9.41691 11.2663 9.51445 11.0573C9.93714 11.3689 10.3707 11.6082 10.8837 11.6082C10.9595 11.1333 10.7717 10.5823 10.5694 10.1947C10.5441 10.1453 10.638 10.0161 10.685 9.98194L10.6814 9.97814ZM9.8974 10.2251C9.68786 10.5025 9.30491 10.5443 9.0448 10.3277C8.78468 10.1111 8.74133 9.70455 8.94725 9.43097C9.15318 9.15739 9.53974 9.11179 9.79985 9.32838C10.0636 9.54876 10.1033 9.95154 9.8974 10.2251Z"   fill={active ? COLORS.primary : '#9ca3af'}/>
    </g>
    </g>
    <defs>
    <clipPath id="clip0_2461_6427">
    <rect width="24" height="24" fill="white"/>
    </clipPath>
    <clipPath id="clip1_2461_6427">
    <rect width="20" height="23" fill="white" transform="translate(2)"/>
    </clipPath>
    </defs>
    </svg>

  );

  const CelebrateIcon = ({ active }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <path d="M18.6188 7.71016C18.885 7.44388 18.885 7.01217 18.6188 6.7459C18.3525 6.47962 17.9208 6.47962 17.6545 6.7459C17.3882 7.01217 17.3882 7.44389 17.6545 7.71016C17.9208 7.97643 18.3525 7.97643 18.6188 7.71016Z" fill={active ? COLORS.primary : '#9ca3af'} />
      <path d="M20.1239 12.433C20.4887 12.3396 20.7087 11.9681 20.6152 11.6033C20.5218 11.2385 20.1503 11.0186 19.7855 11.112C19.4207 11.2055 19.2008 11.577 19.2942 11.9418C19.3877 12.3065 19.7592 12.5265 20.1239 12.433Z" fill={active ? COLORS.primary : '#9ca3af'} />
      <path d="M21.3468 6.34627C21.6131 6.07999 21.6131 5.64828 21.3468 5.38201C21.0805 5.11573 20.6488 5.11573 20.3825 5.382C20.1162 5.64828 20.1162 6.07999 20.3825 6.34627C20.6488 6.61254 21.0805 6.61254 21.3468 6.34627Z" fill={active ? COLORS.primary : '#9ca3af'} />
      <path d="M13.0278 15.693C13.2725 16.2774 13.3641 16.7404 13.3641 17.0006C12.7671 16.9889 10.9969 16.4519 9.27309 14.728C7.54924 13.0042 7.01216 11.234 7.00046 10.637C7.37135 10.637 8.15651 10.8246 9.11664 11.3768C8.92949 11.8856 8.70091 12.3769 8.43285 12.8482C8.68093 13.2108 8.98359 13.5919 9.35009 13.9711C10.4784 12.1854 11.0915 10.1175 11.0915 7.90916C11.0915 6.02594 10.6333 4.0243 9.7668 2.36625C9.59232 2.0324 9.18048 1.90325 8.84663 2.07773C8.51278 2.25221 8.38363 2.66404 8.55811 2.99789C9.32329 4.46196 9.72781 6.24526 9.72781 7.90916C9.72781 8.6563 9.64739 9.38347 9.49338 10.0863C8.03418 9.28599 6.71535 9.1042 6.09151 9.72755C5.97941 9.83965 5.88437 10.0019 5.8371 10.1282C5.83661 10.1282 5.47839 11.1877 4.98176 12.6572C5.01831 12.8765 5.0841 13.1158 5.18255 13.3643C5.5861 14.3844 6.42926 15.5205 7.45518 16.5464L6.54623 17.4554C5.63484 16.544 4.85211 15.5478 4.33062 14.5837C4.07768 15.3324 3.81498 16.1092 3.56155 16.8603C3.99385 17.5202 4.55384 18.1903 5.18304 18.8195L4.27409 19.7285C3.83301 19.2874 3.42167 18.8259 3.05565 18.3585C2.46788 20.0935 2.02242 21.4119 2.02242 21.4119C2.00926 21.4548 2 21.4991 2 21.5464C2 21.6721 2.05069 21.7857 2.13305 21.8676C2.21542 21.9499 2.32898 22.0006 2.45423 22.0006C2.50151 22.0006 2.54586 21.9914 2.58875 21.9782C2.58875 21.9782 13.8724 18.166 13.8724 18.164C13.9982 18.1167 14.1605 18.0217 14.2731 17.9096C14.7775 17.4052 14.7546 16.446 14.3047 15.326C13.8666 15.4079 13.4523 15.5283 13.0278 15.6925V15.693Z" fill={active ? COLORS.primary : '#9ca3af'} />
      <path d="M13.1092 10.892C13.2423 11.0251 13.4168 11.0918 13.5913 11.0918C13.7657 11.0918 13.9402 11.0251 14.0733 10.892C16.2538 8.71151 17.4552 5.79555 17.4552 2.6822C17.4552 2.30546 17.1501 2.00037 16.7733 2.00037C16.3966 2.00037 16.0915 2.30546 16.0915 2.6822C16.0915 5.43148 15.0324 8.00433 13.1092 9.92751C12.8431 10.1936 12.8431 10.6254 13.1092 10.892Z" fill={active ? COLORS.primary : '#9ca3af'} />
      <path d="M21.6345 14.2344C19.9759 13.3679 17.9748 12.9097 16.0916 12.9097C14.9838 12.9097 14.047 13.0096 13.1084 13.2718C12.7843 12.8425 12.4143 12.4146 12.0006 12.0008C11.8256 11.8258 11.6482 11.6596 11.4688 11.5002C11.3338 11.9452 11.172 12.3814 10.9873 12.8084C11.0219 12.842 11.0565 12.8756 11.0911 12.9102C11.3675 13.1866 11.6107 13.4605 11.828 13.7295C11.7145 13.7787 11.5999 13.8294 11.4839 13.884C11.1428 14.0439 10.9961 14.4498 11.1559 14.791C11.3158 15.1322 11.7218 15.2789 12.0625 15.119C13.4388 14.4742 14.5588 14.2744 16.0911 14.2744C17.755 14.2744 19.5383 14.6789 21.0028 15.4441C21.1037 15.4967 21.2114 15.5216 21.3182 15.5216C21.5638 15.5216 21.8012 15.3885 21.923 15.1556C22.0975 14.8217 21.9683 14.4099 21.6345 14.2354V14.2344Z" fill={active ? COLORS.primary : '#9ca3af'} />
    </svg>
  );

  const CompareIcon = ({ active }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <path d="M20.5555 6.31024C20.1194 5.89659 19.413 5.89659 18.9769 6.31024L16 9.13378C15.5639 9.54743 15.5639 10.2175 16 10.6311C16.4361 11.0448 17.1425 11.0448 17.5786 10.6311L18.65 9.61492V14.1176C18.65 15.0909 17.8156 15.8823 16.7895 15.8823H8.07001C7.04388 15.8823 6.20946 15.0909 6.20946 14.1176V9.61492L7.28084 10.6311C7.67707 11.0069 8.43557 11.0332 8.85948 10.6311C9.29559 10.2175 9.29559 9.54743 8.85948 9.13378L5.8823 6.31024C5.44619 5.89659 4.73978 5.89659 4.30367 6.31024L1.32708 9.13378C0.890972 9.54743 0.890972 10.2175 1.32708 10.6311C1.7632 11.0448 2.46961 11.0448 2.90572 10.6311L3.9771 9.61492V14.1176C3.9771 16.2587 5.81294 18 8.07031 18H16.7898C19.0471 18 20.883 16.2587 20.883 14.1176V9.61492L21.9544 10.6311C22.3857 11.0402 23.1019 11.04 23.533 10.6311C23.964 10.2223 23.9691 9.54743 23.533 9.13378L20.5555 6.31024Z" fill={active ? COLORS.primary : '#9ca3af'} />
    </svg>
  );

  const ExploreIcon = ({ active }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <line x1="4" y1="14" x2="12" y2="13" stroke={active ? COLORS.primary : '#9ca3af'} strokeWidth="2" strokeLinecap="round"/>
      <line x1="12" y1="13" x2="18" y2="8" stroke={active ? COLORS.primary : '#9ca3af'} strokeWidth="2" strokeLinecap="round"/>
      <line x1="12" y1="13" x2="22" y2="16" stroke={active ? COLORS.primary : '#9ca3af'} strokeWidth="2" strokeLinecap="round"/>
      <circle cx="4"  cy="14" r="2" fill={active ? COLORS.primary : '#9ca3af'} />
      <circle cx="12" cy="13" r="2" fill={active ? COLORS.primary : '#9ca3af'} />
      <circle cx="18" cy="8"  r="2" fill={active ? COLORS.primary : '#9ca3af'} />
      <circle cx="22" cy="16" r="2" fill={active ? COLORS.primary : '#9ca3af'} />
    </svg>
  );

  const isCatalystUser = getLoggedInUser()?.toLowerCase().includes('catalyst');
  const isDevUser = ['sd@sd.org', 'aa@aa.org', 'ss@ss.org', 'as@as.org'].includes(getLoggedInUser()?.toLowerCase());

  const TABS = [
    { label: 'Introduce Talk Trees', Icon: IntroIcon },
    { label: 'Celebrate', Icon: CelebrateIcon },
    { label: 'Compare', Icon: CompareIcon },
    ...(isCatalystUser ? [{ label: 'Explore My Talk', Icon: ExploreIcon }] : []),
    ...(isDevUser ? [{ label: 'Explore My Talk', Icon: ExploreIcon }] : []),
  ];

  const libraryLabel = (
  <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 400, color: COLORS.text, fontSize: '1.25rem' }}>
    Talk Trees Library
  </Typography>
);

  return (
    <ThemeProvider theme={muiTheme}>
      <Box component="main" aria-label="Talk Trees Library">
        {showWelcomeBanner && (
          <Box
            onClick={handleDismissWelcome}
            sx={{ position: 'fixed', inset: 0, zIndex: 9 }}
          />
        )}
        {showWelcomeBanner && (
          <Box
            role="status"
            aria-live="polite"
            aria-label="Welcome message"
            sx={{
              position: 'fixed',
              top: '70px',
              right: '12px',
              zIndex: 10,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              backgroundColor: COLORS.primary,
              color: '#fff',
              borderRadius: '12px',
              p: isMobile ? '14px 16px' : '16px 20px',
              maxWidth: '320px',
              width: isMobile ? 'calc(100% - 24px)' : '320px',
              boxShadow: '0 4px 20px rgba(31,114,231,0.4)',
            }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '14px', mb: 0.5, color: '#fff', fontFamily: FONT_FAMILY }}>
                Welcome to Talk Trees!
              </Typography>
              <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.92)', lineHeight: 1.5, fontFamily: FONT_FAMILY }}>
                Check out the sample Talk Trees below to explore the interface. When you’re ready, you can create your own Talk Tree by clicking on “Create a Tree” in the menu.
              </Typography>
            </Box>
            <Box
              component="button"
              onClick={handleDismissWelcome}
              aria-label="Dismiss welcome message"
              sx={{
                flexShrink: 0,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#fff',
                p: '2px',
                mt: '1px',
                opacity: 0.85,
                lineHeight: 0,
                '&:hover': { opacity: 1 },
                '&:focus-visible': { outline: '2px solid rgba(255,255,255,0.7)', borderRadius: '4px' },
              }}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </Box>
          </Box>
        )}

        {noDataAvailable ? (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '60vh', textAlign: 'center', gap: 2 }}
            role="status"
          >
            <Typography variant="h6" sx={{ fontWeight: 400, color: '#111827', fontFamily: FONT_FAMILY }}>
              No Talk Trees available yet
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.textMuted, fontFamily: FONT_FAMILY }}>
              Upload a recording to generate your first Talk Tree.
            </Typography>
          </Box>
        ) : (isLoading || !data.length) ? (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '60vh' }}
            role="status"
            aria-label="Loading your Talk Trees"
          >
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <style>{`@keyframes tt-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              <path d="M52 28C52 31.1517 51.3792 34.2726 50.1731 37.1844C48.967 40.0962 47.1992 42.742 44.9706 44.9706C42.742 47.1992 40.0962 48.967 37.1844 50.1731C34.2726 51.3792 31.1517 52 28 52C24.8483 52 21.7274 51.3792 18.8156 50.1731C15.9038 48.967 13.258 47.1992 11.0294 44.9706C8.80083 42.742 7.033 40.0962 5.82689 37.1844C4.62078 34.2726 4 31.1517 4 28C4 24.8483 4.62078 21.7274 5.82689 18.8156C7.03301 15.9038 8.80083 13.258 11.0294 11.0294C13.258 8.80083 15.9038 7.033 18.8156 5.82689C21.7274 4.62078 24.8483 4 28 4C31.1517 4 34.2726 4.62078 37.1844 5.82689C40.0962 7.03301 42.742 8.80083 44.9706 11.0294C47.1992 13.258 48.967 15.9038 50.1731 18.8156C51.3792 21.7274 52 24.8483 52 28L52 28Z" stroke="#E7E3DF" strokeWidth="8"/>
              <path d="M52 28C52 31.1517 51.3792 34.2726 50.1731 37.1844C48.967 40.0962 47.1992 42.742 44.9706 44.9706C42.742 47.1992 40.0962 48.967 37.1844 50.1731C34.2726 51.3792 31.1517 52 28 52C24.8483 52 21.7274 51.3792 18.8156 50.1731C15.9038 48.967 13.258 47.1992 11.0294 44.9706C8.80083 42.742 7.033 40.0962 5.82689 37.1844C4.62078 34.2726 4 31.1517 4 28" stroke="#4C8EEC" strokeWidth="8" strokeLinecap="round" style={{ transformOrigin: '28px 28px', animation: 'tt-spin 1s linear infinite' }}/>
            </svg>
            <Typography variant="h6" sx={{ mt: 3, fontWeight: 500, color: '#3a3a3a' }}>
              Loading your Talk Trees...
            </Typography>
          </Box>
        ) : (
          <>
            {analyticsLoading ? (
              <Box
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', flexDirection: 'column' }}
                role="status"
                aria-label="Loading analytics data"
              >
                <CircularProgress size={40} sx={{ mb: 2 }} aria-hidden="true" />
                <Typography variant="subtitle1">Loading analytics data...</Typography>
              </Box>
            ) : (
              <>
                {analyticsNotFoundError && (
                  <Typography color="error" sx={{ mb: 2 }} role="alert">{analyticsNotFoundError}</Typography>
                )}

                {selectedPeriod && analyticstSessionID && showGraphs && (
                  <Box sx={{ mb: 4, position: 'relative' }}>
                    <SessionTrees
                      processTranscriptData={processTranscriptData}
                      analyticsSessionID={analyticstSessionID}
                      previousSessionIds={previousSessionIds}
                      isStudentEngagementPage={true}
                      region={selectedRegion}
                      key={`${analyticstSessionID}-${selectedPeriod}`}
                      client={client}
                      selectedTrees={mainTabIndex === 2 ? new Set([leftTree, rightTree].filter(Boolean)) : new Set([leftTree].filter(Boolean))}
                      onTreeSelect={onTreeSelect}
                      selectionMode={mainTabIndex === 1 ? 'single' : mainTabIndex === 2 ? 'compare' : mainTabIndex === 3 ? 'single' : 'none'}
                      label={libraryLabel}
                      showDescriptionOnly={true}
                      highlightedTree={highlightedTree}
                      treeLabels={mainTabIndex === 2 ? {
                        [leftTree]: 'Selection 1',
                        [rightTree]: 'Selection 2',
                      } : mainTabIndex === 1 ? {
                        [leftTree]: 'Selected',
                      } : {}}
                      onManageTrees={() => {
                        mixpanel.track('manageTreesClicked');
                        setActiveMenu(MENU_IDS.RECORDINGS);
                      }}
                    />
                  </Box>
                )}

                {selectedPeriod && analyticstSessionID && showGraphs && (
                  <Paper elevation={0} sx={{ backgroundColor: 'transparent' }}>

                    {/* Tab bar */}
                    <Box sx={{ borderBottom: `1px solid ${COLORS.hairline}` }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: isMobile ? 'center' : 'flex-end',
                          justifyContent: 'space-between',
                          flexDirection: isMobile ? 'column' : 'row',
                          gap: isMobile ? 1.5 : 0,
                        }}
                      >
                        {!isMobile && (
                          <Typography sx={{ fontWeight: 400, fontSize: '20px', color: '#111827', pb: 1, fontFamily: FONT_HEADING }}>
                            What Can I Do With Talk Trees?
                          </Typography>
                        )}

                        {/* Tablist */}
                        <Box
                          role="tablist"
                          aria-label="Talk Tree actions"
                          sx={{
                            display: 'flex',
                            width: isMobile ? '100%' : 'auto',
                            overflowX: isMobile ? 'auto' : 'visible',
                            WebkitOverflowScrolling: 'touch',
                          }}
                        >
                          {TABS.map(({ label, Icon }, i) => {
                            const active = mainTabIndex === i;
                            return (
                              <Box
                                key={i}
                                role="tab"
                                aria-selected={active}
                                aria-controls={`tabpanel-${i}`}
                                id={`tab-${i}`}
                                tabIndex={active ? 0 : -1}
                                onClick={() => handleTabChange(i)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTabChange(i); }
                                  if (e.key === 'ArrowRight') { e.preventDefault(); handleTabChange((i + 1) % TABS.length); }
                                  if (e.key === 'ArrowLeft') { e.preventDefault(); handleTabChange((i - 1 + TABS.length) % TABS.length); }
                                }}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: isMobile ? '8px 12px' : '8px 20px',
                                  cursor: 'pointer',
                                  borderBottom: active ? `2px solid ${COLORS.primary}` : '2px solid transparent',
                                  transition: 'border-color 0.15s ease',
                                  flexShrink: 0,
                                  minWidth: isMobile ? '80px' : 'auto',
                                  '&:hover': { borderBottomColor: active ? COLORS.primary : '#d1d5db' },
                                  '&:focus-visible': { outline: `3px solid ${COLORS.primary}`, outlineOffset: '-2px', borderRadius: '4px 4px 0 0' },
                                }}
                              >
                                <Icon active={active} />
                                <Typography sx={{
                                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                                  fontWeight: active ? 600 : 400,
                                  color: active ? COLORS.primary : COLORS.textMuted,
                                  fontFamily: FONT_FAMILY,
                                  whiteSpace: 'nowrap',
                                }}>
                                  {label}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>

                    {/* Tab panels */}
                    <Box sx={{ mt: '24px' }}>
                      {TABS.map((_, i) => (
                        <Box
                          key={i}
                          role="tabpanel"
                          id={`tabpanel-${i}`}
                          aria-labelledby={`tab-${i}`}
                          hidden={mainTabIndex !== i}
                        >
                          {mainTabIndex === i && (
                            <>
                              {i === 0 && renderIntroductionTab()}
                              {i === 1 && renderCelebrateTab()}
                              {i === 2 && renderCompareTab()}
                              {i === 3 && (
                                <ExploreMyTalk
                                  sessionData={data.find(item => item.sessionId === selectedFeedbackTree)}
                                  tutorTalkMoves={tutorTalkMoves}
                                  studentTalkMoves={studentTalkMoves}
                                  mathTopicsCovered={mathTopicsCovered}
                                  feedbackSessionHighlights={feedbackSessionHighlights}
                                  studentParticipation={studentParticipation}
                                  talkMovesLabels={talkMovesLabels}
                                  selectedTreeImage={feedbackTreeImage}
                                  processTranscriptData={processTranscriptData}
                                  selectedTreeId={selectedFeedbackTree}
                                  onTreeSelect={handleFeedbackTreeSelection}
                                  allSessionIds={[selectedFeedbackTree || leftTree, ...previousSessionIds].filter(Boolean)}
                                />
                              )}
                            </>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default TalkTreeLibrary;